import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

interface PriceData {
  currentPrice: number;
  priceChange24h: number;
  volume24h: number;
  marketCap: number;
  lastUpdated: Date;
  priceHistory: Array<{
    time: string;
    price: number;
  }>;
}

// 模拟价格数据获取函数
const fetchPriceData = async (): Promise<PriceData> => {
  // 模拟API调用延迟
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // 模拟价格数据 - 在实际项目中这里会调用Chainlink oracle或DEX API
  const basePrice = 0.99; // 基础价格
  const variation = (Math.random() - 0.5) * 0.1; // ±5% 随机变化
  const currentPrice = Math.max(0.90, Math.min(1.10, basePrice + variation));
  
  const priceChange24h = (Math.random() - 0.5) * 4; // ±2% 24小时变化
  
  // 生成24小时价格历史数据
  const priceHistory = [];
  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const time = new Date(now.getTime() - i * 60 * 60 * 1000);
    const historicalPrice = currentPrice + (Math.random() - 0.5) * 0.02; // ±1% 随机变化
    priceHistory.push({
      time: time.toISOString(),
      price: Number(historicalPrice.toFixed(4)),
    });
  }

  return {
    currentPrice: Number(currentPrice.toFixed(4)),
    priceChange24h: Number(priceChange24h.toFixed(2)),
    volume24h: Math.random() * 1000000 + 500000, // 50万-150万
    marketCap: 1000000000 * currentPrice, // 基于10亿总供应量
    lastUpdated: new Date(),
    priceHistory,
  };
};

export const usePriceData = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dorPrice'],
    queryFn: fetchPriceData,
    refetchInterval: 10000, // 每10秒更新一次
    staleTime: 5000, // 5秒内认为数据新鲜
    refetchOnWindowFocus: true,
  });

  return {
    priceData: data,
    isLoading,
    error,
    refetch,
  };
};

// 价格状态判断Hook
export const usePriceStatus = (currentPrice: number) => {
  const getPriceStatus = () => {
    if (currentPrice >= 0.99 && currentPrice <= 1.01) {
      return {
        status: 'stable' as const,
        riskLevel: 'low' as const,
        message: '价格稳定，锚定正常',
        color: 'green' as const,
      };
    }
    
    if (currentPrice >= 0.95 && currentPrice < 0.99) {
      return {
        status: 'warning' as const,
        riskLevel: 'medium' as const,
        message: '价格轻微偏离，建议关注',
        color: 'yellow' as const,
      };
    }
    
    if (currentPrice < 0.95) {
      return {
        status: 'critical' as const,
        riskLevel: 'high' as const,
        message: '价格严重偏离，风险较高',
        color: 'red' as const,
      };
    }
    
    if (currentPrice > 1.01 && currentPrice <= 1.05) {
      return {
        status: 'opportunity' as const,
        riskLevel: 'medium' as const,
        message: '价格高于锚定，存在套利机会',
        color: 'blue' as const,
      };
    }
    
    return {
      status: 'extreme' as const,
      riskLevel: 'high' as const,
      message: '价格显著偏离，投机风险',
      color: 'red' as const,
    };
  };

  return getPriceStatus();
};

// 价格趋势分析Hook
export const usePriceTrend = (priceHistory: number[] = []) => {
  const analyzeTrend = () => {
    if (priceHistory.length < 2) {
      return { trend: 'stable', direction: 'none', volatility: 'low' };
    }

    const recent = priceHistory.slice(-5); // 最近5个价格点
    const older = priceHistory.slice(-10, -5); // 前5个价格点
    
    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
    
    const change = recentAvg - olderAvg;
    const changePercent = (change / olderAvg) * 100;
    
    // 计算波动性
    const variance = recent.reduce((sum, price) => {
      return sum + Math.pow(price - recentAvg, 2);
    }, 0) / recent.length;
    const volatility = Math.sqrt(variance);
    
    let trend: 'rising' | 'falling' | 'stable';
    let direction: 'up' | 'down' | 'none';
    let volatilityLevel: 'low' | 'medium' | 'high';
    
    if (changePercent > 1) {
      trend = 'rising';
      direction = 'up';
    } else if (changePercent < -1) {
      trend = 'falling';
      direction = 'down';
    } else {
      trend = 'stable';
      direction = 'none';
    }
    
    if (volatility < 0.01) {
      volatilityLevel = 'low';
    } else if (volatility < 0.03) {
      volatilityLevel = 'medium';
    } else {
      volatilityLevel = 'high';
    }
    
    return {
      trend,
      direction,
      volatility: volatilityLevel,
      changePercent: Number(changePercent.toFixed(2)),
    };
  };

  return analyzeTrend();
}; 